import {
    VStack,
    HStack,
    Text,
    SimpleGrid,
    GridItem,
    Link,
    Badge,
    Icon,
    Tooltip,
    Box,
    type BoxProps,
} from '@chakra-ui/react';
import { FiExternalLink } from 'react-icons/fi';
import { ProjectType } from '~/components/Layout/ProjectType';
import { format, parse } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import { join } from '~/utils';
import { generateProjectNumber } from '~/utils/generateProjectNumber';
import { Controls } from './Renderers';

type FollowUp = {
    blocked: boolean;
    project?: {
        id: number;
        lfdNr: number;
        anlageDatum: string;
        niederlassungNummer: number;
        subprojectId: number;
        subprojectLfdNr: number;
    } | null;
} | null;

// UCPWO-423 — Markierung eines Folgeauftrags. Bewusst kein `ProjectType`: das ist das Gewerk,
// ein Folgeauftrag ist keins. Gleiche Form wie die Gewerk-Chips, eigene Farbe.
const FollowUpBadge = ({ children, ...props }: BoxProps) => (
    <Box
        rounded="sm"
        width="fit-content"
        px={2}
        bg="#9F7AEA"
        color="white"
        fontSize={14}
        fontWeight="semibold"
        lineHeight={5}
        display="inline-flex"
        alignItems="center"
        gap={1}
        {...props}
    >
        {children}
    </Box>
);

// Unter der Schadennummer. Gibt es das Projekt schon, ist der Chip ein Link auf das
// Subprojekt, zu dem der Auftrag kommt (Nummer im Tooltip); sonst wartet der Auftrag auf
// den ersten (`blocked`, dann ohne Menü, siehe `ServiceRequestControls`).
const FollowUpMark = ({ followUp }: { followUp: NonNullable<FollowUp> }) => {
    const { project } = followUp;

    if (!project) {
        return (
            <FollowUpBadge
                title={
                    followUp.blocked
                        ? 'Erst den ersten Auftrag dieser Schadennummer annehmen oder ablehnen'
                        : undefined
                }
                data-test-id="service-request-follow-up"
            >
                Folgeauftrag
            </FollowUpBadge>
        );
    }

    const number = generateProjectNumber(
        project.anlageDatum,
        project.niederlassungNummer,
        project.lfdNr,
        project.subprojectLfdNr
    );

    return (
        <Tooltip label={`Folgeauftrag zu ${number}`} placement="top" hasArrow>
            <Link
                as={RouterLink}
                to={`/projekte/${project.id}/${project.subprojectId}/details`}
                _hover={{ textDecoration: 'none', opacity: 0.85 }}
                aria-label={`Folgeauftrag zu ${number}`}
                data-test-id="service-request-follow-up"
            >
                <FollowUpBadge>
                    Folgeauftrag
                    <Icon as={FiExternalLink} boxSize={3} />
                </FollowUpBadge>
            </Link>
        </Tooltip>
    );
};

export const ServiceRequestAddress = ({ row }: any) => {
    const { client = {}, branch = {}, damageNumber, followUp } = row?.original || {};
    return (
        <SimpleGrid columns={2} w="300px">
            <GridItem>Niederlassung:</GridItem>
            <GridItem>{`${branch.number} - ${branch.description}`}</GridItem>
            <GridItem>Auftraggeber:</GridItem>
            <GridItem>{client.name}</GridItem>
            <GridItem>Objekt:</GridItem>
            <GridItem>
                <VStack align="start" spacing={0}>
                    <Text>{client.address?.street}</Text>
                    <Text>{join([client.address?.postalCode, client.address?.city], ' ')}</Text>
                </VStack>
            </GridItem>
            <GridItem>Schaden-Nr.:</GridItem>
            <GridItem>
                <VStack align="start" spacing={1}>
                    <Text>{damageNumber}</Text>
                    {followUp && <FollowUpMark followUp={followUp} />}
                </VStack>
            </GridItem>
        </SimpleGrid>
    );
};

// Kürzel der Connectors, so mit dem Kunden abgestimmt: PXC (PX Connect) und SP
// (ServicePool, über den die Gothaer-Aufträge kommen). Der volle Name steht im Tooltip.
const INTEGRATION: Record<string, { short: string; long: string }> = {
    gothaer: { short: 'SP', long: 'ServicePool' },
    pxconnect: { short: 'PXC', long: 'PX Connect' },
};

type SourceSystem = { source?: string | null; name?: string | null } | null | undefined;

export const integrationLabel = (sourceSystem?: SourceSystem) =>
    (sourceSystem?.source && INTEGRATION[sourceSystem.source]?.short) ||
    sourceSystem?.name ||
    sourceSystem?.source ||
    '';

const integrationTitle = (sourceSystem?: SourceSystem) =>
    (sourceSystem?.source && INTEGRATION[sourceSystem.source]?.long) || undefined;

// Verbindung, von der SVC den Auftrag geholt hat (UCPWO-398). Nur was KEIN echter
// Auftrag ist, bekommt eine Badge — prod ist der Normalfall und bleibt unmarkiert.
// Aufträge von vor dem Verbindungs-Stempel ebenfalls ohne Badge: keine Ableitung aus
// der Stage, der Endpunkt wird im SVC-Admin gepflegt.
const VERBINDUNG: Record<string, { label: string; colorScheme: string }> = {
    test: { label: 'test', colorScheme: 'yellow' },
    emulate: { label: 'emulate', colorScheme: 'orange' },
};

type OriginProps = {
    insurer?: { number?: string | null; name?: string | null } | null;
    sourceSystem?: SourceSystem;
    connection?: { kind: string; host: string } | null;
};

/**
 * Herkunft eines Auftrags in einer Zeile: `LVM (PXC) [emulate]`. Dieselbe Darstellung im
 * Auftragseingang und im Kopf der Projektseiten (UCPWO-398). Ohne bekannte Versicherung
 * (Projekte von vor dem Stempel) steht nur das Kürzel da.
 */
export const ServiceRequestOriginView = ({ insurer, sourceSystem, connection }: OriginProps) => {
    const versicherung = insurer?.name || (insurer?.number ? `Nr. ${insurer.number}` : undefined);
    const kuerzel = integrationLabel(sourceSystem);
    const verbindung = connection ? VERBINDUNG[connection.kind] : undefined;

    return (
        // nowrap: im Seitenkopf nimmt die Titelspalte `w="full"` und drückt die Zeile
        // sonst auf Mindestbreite zusammen.
        <HStack spacing={2} whiteSpace="nowrap" data-test-id="service-request-origin">
            <Text>
                {versicherung && (
                    <Text
                        as="span"
                        fontWeight="semibold"
                        title={insurer?.number ? `Versicherer-Nr. ${insurer.number}` : undefined}
                    >
                        {versicherung}{' '}
                    </Text>
                )}
                <Text as="span" title={integrationTitle(sourceSystem)}>
                    {versicherung ? `(${kuerzel})` : kuerzel}
                </Text>
            </Text>
            {verbindung && (
                <Badge
                    colorScheme={verbindung.colorScheme}
                    fontSize="0.7em"
                    title={connection?.host}
                    data-test-id="service-request-connection"
                >
                    {verbindung.label}
                </Badge>
            )}
        </HStack>
    );
};

export const ServiceRequestOrigin = ({ row }: any) => {
    const { insurer, sourceSystem, connection } = row?.original || {};
    return (
        <ServiceRequestOriginView
            insurer={insurer}
            sourceSystem={sourceSystem}
            connection={connection}
        />
    );
};

export const ServiceRequestTypes = ({ row }: any) => {
    const serviceTypes = row?.original?.details?.serviceTypes || [];
    return (
        <VStack align="start" spacing={1}>
            {serviceTypes.map((type: any) => (
                <ProjectType key={type}>{type}</ProjectType>
            ))}
        </VStack>
    );
};

// UCPWO-423 — wo sonst das Menü steht, sagt ein gesperrter Folgeauftrag, worauf er wartet.
// Ohne den Hinweis sähe das fehlende Menü wie ein Fehler oder fehlende Rechte aus.
export const ServiceRequestControls = (props: any) =>
    props.row?.original?.followUp?.blocked ? (
        <Text
            fontSize="xs"
            fontStyle="italic"
            color="gray.500"
            title="Erst den ersten Auftrag dieser Schadennummer annehmen oder ablehnen"
            data-test-id="service-request-waiting"
        >
            wartet auf Erstauftrag
        </Text>
    ) : (
        <Controls {...props} />
    );

export const ServiceRequestAttachments = ({ row }: any) => {
    const { attachments = [] } = row?.original || {};
    return (
        <VStack align="start" spacing={1}>
            {attachments.map(({ filename, url }: { filename: string; url: string }) =>
                url ? (
                    <Link
                        color="blue.500"
                        key={filename}
                        onClick={() => window.open(url, '_blank')}
                    >
                        {filename}
                    </Link>
                ) : (
                    // No presigned URL (e.g. the source order carried no document
                    // bytes, or the file is not in this repo's storage bucket).
                    // Show the name without a dead link that would open about:blank.
                    <Text key={filename} color="gray.400" title="Kein Dokument verfügbar">
                        {filename}
                    </Text>
                )
            )}
        </VStack>
    );
};

export const ServiceRequestDateFormat = ({
    row,
}: {
    row: {
        original: {
            details: {
                receiptDateTime: { format: string; dateTime: string };
            };
        };
    };
}) => {
    const { receiptDateTime } = row?.original?.details || {};
    const hasTime = !!receiptDateTime?.format?.includes('HHmm');
    const date = receiptDateTime?.dateTime
        ? parse(receiptDateTime.dateTime, receiptDateTime.format, new Date())
        : null;
    // Guard against a missing/unparseable date — `format` throws on an invalid
    // Date and would otherwise crash the whole dashboard table.
    const dateTime =
        date && !isNaN(date.valueOf())
            ? hasTime
                ? format(date, 'dd.MM.yyyy, HH:mm')
                : format(date, 'dd.MM.yyyy')
            : '';
    const width = hasTime ? 130 : 90;

    return (
        <VStack align="start" spacing={1}>
            <Text w={width}>{dateTime}</Text>{' '}
        </VStack>
    );
};
